//hooks/useProactiveRefresh.js
import { useEffect, useRef, useCallback } from 'react';
import {
  attemptTokenRefresh,
  forceLogoutAndRedirect,
  getAccessTokenExpiresAt,
} from '../services/apiClient';

const REFRESH_BUFFER_RATIO = 0.2; // refresh when 20% lifetime remains
const FALLBACK_LIFETIME_MS = 24 * 60 * 60 * 1000;

const getRefreshDelay = () => {
  const expiry = getAccessTokenExpiresAt();
  if (!expiry) return null;

  const now = Date.now();
  const remaining = expiry - now;
  if (remaining <= 0) return 0;

  const buffer = FALLBACK_LIFETIME_MS * REFRESH_BUFFER_RATIO;
  return Math.max(remaining - buffer, 0);
};

export const useProactiveRefresh = (isAuthenticated) => {
  const timeoutRef = useRef(null);
  const isRefreshingRef = useRef(false);

  const scheduleRefresh = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const delay = getRefreshDelay();
    if (delay === null) return;

    console.log('🔄 Next proactive refresh in:', Math.round(delay / 1000), 'sec');

    timeoutRef.current = setTimeout(async () => {
      if (isRefreshingRef.current) return;

      isRefreshingRef.current = true;
      try {
        console.log('🔄 Attempting proactive token refresh...');
        await attemptTokenRefresh();
        console.log('✅ Token refresh successful');
        scheduleRefresh();
      } catch (error) {
        console.error('🔒 Proactive refresh failed:', error.message);
        forceLogoutAndRedirect();
      } finally {
        isRefreshingRef.current = false;
      }
    }, delay);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    scheduleRefresh();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        const delay = getRefreshDelay();
        if (delay === 0) {
          console.log('⚠️ Token within refresh buffer, triggering immediate refresh');
          scheduleRefresh();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [isAuthenticated, scheduleRefresh]);
};
